// src/components/Footer.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Leaf, Phone, Mail, MapPin, Smartphone } from 'lucide-react';
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube } from 'react-icons/fa';

const Footer = () => {
  const [settings, setSettings] = useState({
    siteName: 'NAVIROSE',
    logo: '',
    phone: '+92 300 1234567',
    email: 'hello@navirose.com',
    address: 'Lahore, Pakistan',
    footerAbout: 'Premium quality fresh organic products sourced directly from certified organic farms.',
    footerFacebook: '',
    footerInstagram: '',
    footerTwitter: '',
    footerYoutube: '',
    footerCopyright: '© 2026 NAVIROSE. All rights reserved.',
  });
  const [showAppLink, setShowAppLink] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(prev => ({ ...prev, ...docSnap.data() }));
        }

        const appRef = doc(db, 'settings', 'app-download');
        const appSnap = await getDoc(appRef);
        if (appSnap.exists() && appSnap.data().active !== false) {
          setShowAppLink(true);
        }
      } catch (error) {
        console.error('Error fetching footer settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="mt-10"
      style={{ backgroundColor: 'var(--color-footer-bg)', color: 'var(--color-footer-text)' }}>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">

        {/* About */}
        <div className="sm:col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            {settings.logo ? (
              <img src={settings.logo} alt={settings.siteName} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : (
              <>
                <Leaf size={28} style={{ color: '#F5AEC0' }} />
                <span className="text-xl font-bold" style={{ color: '#F5AEC0' }}>
                  {settings.siteName}
                </span>
              </>
            )}
          </div>
          <p className="text-sm opacity-80 leading-relaxed">
            {settings.footerAbout}
          </p>
          <div className="flex gap-3 mt-4">
            {settings.footerFacebook && (
              <a href={settings.footerFacebook} target="_blank" rel="noopener noreferrer">
                <FaFacebook size={18} className="hover:opacity-70 cursor-pointer transition" style={{ color: '#F5AEC0' }} />
              </a>
            )}
            {settings.footerInstagram && (
              <a href={settings.footerInstagram} target="_blank" rel="noopener noreferrer">
                <FaInstagram size={18} className="hover:opacity-70 cursor-pointer transition" style={{ color: '#F5AEC0' }} />
              </a>
            )}
            {settings.footerTwitter && (
              <a href={settings.footerTwitter} target="_blank" rel="noopener noreferrer">
                <FaTwitter size={18} className="hover:opacity-70 cursor-pointer transition" style={{ color: '#F5AEC0' }} />
              </a>
            )}
            {settings.footerYoutube && (
              <a href={settings.footerYoutube} target="_blank" rel="noopener noreferrer">
                <FaYoutube size={18} className="hover:opacity-70 cursor-pointer transition" style={{ color: '#F5AEC0' }} />
              </a>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-base md:text-lg font-bold mb-4" style={{ color: '#F5AEC0' }}>
            Quick Links
          </h3>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/" className="hover:opacity-100 transition hover:text-pink-300">Home</Link></li>
            <li><Link to="/shop" className="hover:opacity-100 transition hover:text-pink-300">Shop</Link></li>
            <li><Link to="/about" className="hover:opacity-100 transition hover:text-pink-300">About Us</Link></li>
            <li><Link to="/contact" className="hover:opacity-100 transition hover:text-pink-300">Contact</Link></li>
            {showAppLink && (
              <li>
                <Link to="/app-download" className="hover:opacity-100 transition hover:text-pink-300 flex items-center gap-1">
                  <Smartphone size={12} /> Download App
                </Link>
              </li>
            )}
          </ul>
        </div>

        {/* Customer Service */}
        <div>
          <h3 className="text-base md:text-lg font-bold mb-4" style={{ color: '#F5AEC0' }}>
            Customer Service
          </h3>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/profile" className="hover:opacity-100 transition hover:text-pink-300">My Profile</Link></li>
            <li><Link to="/profile/wishlist" className="hover:opacity-100 transition hover:text-pink-300">Wishlist</Link></li>
            <li><Link to="/cart" className="hover:opacity-100 transition hover:text-pink-300">Cart</Link></li>
            <li><Link to="/checkout" className="hover:opacity-100 transition hover:text-pink-300">Checkout</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-base md:text-lg font-bold mb-4" style={{ color: '#F5AEC0' }}>
            Contact Us
          </h3>
          <ul className="space-y-3 text-sm opacity-80">
            {settings.phone && (
              <li className="flex items-center gap-2">
                <Phone size={16} style={{ color: '#F5AEC0' }} /> {settings.phone}
              </li>
            )}
            {settings.email && (
              <li className="flex items-center gap-2">
                <Mail size={16} style={{ color: '#F5AEC0' }} /> {settings.email}
              </li>
            )}
            {settings.address && (
              <li className="flex items-center gap-2">
                <MapPin size={16} style={{ color: '#F5AEC0' }} /> {settings.address}
              </li>
            )}
          </ul>
        </div>

      </div>

      <div className="py-4 px-4 text-center text-xs md:text-sm"
        style={{ borderTop: `1px solid rgba(245, 174, 192, 0.2)`, color: '#F5AEC0' }}>
        {settings.footerCopyright}
      </div>
    </footer>
  );
};

export default Footer;